import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MOIS, MONNAIES, fmtMontant, fmtDate, todayISO } from "@/lib/format";
import { PageHeader, ConfirmDialog, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssujettiCombobox } from "@/components/AssujettiCombobox";
import { Pagination } from "@/components/Pagination";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Loader2, Printer, FileDown } from "lucide-react";

const yr = String(new Date().getFullYear());
const OBSERVATIONS = ["Paiement normal", "Redressement", "Paiement intérieur"];
const EMPTY = { assujetti_id: "", note_taxation: "", date: todayISO(), mois: MOIS[new Date().getMonth()], annee: yr, note: "", observation: "Paiement normal", monnaie: "CDF", montant: "", code_taux: "", taux: "", penalite: "", nuitees_vendues: "", repas_boissons: "" };

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{value || "—"}</span>
    </div>
  );
}

export default function NotesDebit() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [tauxRows, setTauxRows] = useState([]);
  const [selectedAssujetti, setSelectedAssujetti] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirm, setConfirm] = useState(null);
  const [filterRedevable, setFilterRedevable] = useState("");
  const [filterObs, setFilterObs] = useState("all");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE, q: filterRedevable.trim() || undefined,
      observation: filterObs === "all" ? undefined : filterObs };
    const [notes, taux] = await Promise.all([api.get("/notes-debit", { params }), api.get("/taux")]);
    setRows(notes.data.items);
    setMeta({ total: notes.data.total, pages: notes.data.pages });
    setTauxRows(taux.data);
    setLoading(false);
  }, [page, filterRedevable, filterObs]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const filteredRows = rows;

  const selected = selectedAssujetti;
  const isCode1 = String(form.code_taux) === "1";

  // Recherche du taux/code correspondant à l'acte générateur d'un assujetti
  const matchTaux = useCallback((acteId) => tauxRows.find((t) => t.acte_generateur_id === acteId), [tauxRows]);

  // Montant = somme automatique quand Code = 1
  useEffect(() => {
    if (String(form.code_taux) === "1") {
      const sum = (Number(form.penalite) || 0) + (Number(form.nuitees_vendues) || 0) + (Number(form.repas_boissons) || 0);
      setForm((f) => ({ ...f, montant: sum ? String(sum) : "" }));
    }
  }, [form.code_taux, form.penalite, form.nuitees_vendues, form.repas_boissons]);

  const onSelectAssujetti = (a) => {
    const t = matchTaux(a.acte_generateur_id);
    setForm((f) => ({
      ...f, assujetti_id: a.id,
      code_taux: t ? String(t.code ?? "") : "",
      taux: t ? String(t.taux ?? "") : "",
      penalite: "", nuitees_vendues: "", repas_boissons: "",
    }));
    setSelectedAssujetti(a);
  };

  const openNew = () => { setEditing(null); setForm(EMPTY); setSelectedAssujetti(null); setSheet(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({ assujetti_id: n.assujetti_id, note_taxation: n.note_taxation || "", date: (n.date || "").slice(0, 10),
      mois: n.mois, annee: n.annee, note: n.note || "", observation: n.observation || "Paiement normal", monnaie: n.monnaie, montant: n.montant,
      code_taux: n.code_taux != null ? String(n.code_taux) : "", taux: n.taux != null ? String(n.taux) : "",
      penalite: n.penalite != null ? String(n.penalite) : "", nuitees_vendues: n.nuitees_vendues != null ? String(n.nuitees_vendues) : "",
      repas_boissons: n.repas_boissons != null ? String(n.repas_boissons) : "" });
    setSelectedAssujetti({ code: n.code, redevable: n.redevable, province: n.province, ville: n.ville,
      commune: n.commune, quartier: n.quartier, avenue: n.avenue, acte_generateur_id: n.acte_generateur_id, acte_generateur_nom: n.acte_generateur_nom });
    setSheet(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.assujetti_id) { toast.error("Sélectionnez un redevable"); return; }
    if (!form.note_taxation.trim()) { toast.error("La Note de Taxation est obligatoire"); return; }
    if (!form.note.trim()) { toast.error("Le Libellé est obligatoire"); return; }
    if (!form.montant || Number(form.montant) <= 0) { toast.error("Montant invalide"); return; }
    const payload = {
      ...form,
      montant: Number(form.montant),
      code_taux: form.code_taux || "",
      taux: form.taux === "" || form.taux == null ? null : Number(form.taux),
      penalite: isCode1 && form.penalite !== "" ? Number(form.penalite) : null,
      nuitees_vendues: isCode1 && form.nuitees_vendues !== "" ? Number(form.nuitees_vendues) : null,
      repas_boissons: isCode1 && form.repas_boissons !== "" ? Number(form.repas_boissons) : null,
    };
    try {
      if (editing) await api.put(`/notes-debit/${editing.id}`, payload);
      else await api.post("/notes-debit", payload);
      toast.success(editing ? "Note modifiée" : "Note de perception créée");
      setSheet(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/notes-debit/${confirm.id}`);
      toast.success("Note supprimée");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setConfirm(null);
    }
  };

  return (
    <div data-testid="notes-debit-page">
      <PageHeader title="Notes de Perception" subtitle="Émission et suivi des notes de perception">
        <Button variant="outline" onClick={() => window.open(`${API}/notes-debit/export/excel`, "_blank")} data-testid="export-notes-btn">
          <FileDown className="mr-1.5 h-4 w-4" /> Export Excel
        </Button>
        {canWrite && (
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="add-note-debit-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Nouvelle note
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input value={filterRedevable} onChange={(e) => { setFilterRedevable(e.target.value); setPage(1); }}
          placeholder="Filtrer par redevable, code, n° ou taxation…" className="max-w-md" data-testid="filter-redevable" />
        <Select value={filterObs} onValueChange={(v) => { setFilterObs(v); setPage(1); }}>
          <SelectTrigger className="w-56" data-testid="filter-observation"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les observations</SelectItem>
            {OBSERVATIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200 shadow-sm">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : filteredRows.length === 0 ? (
          <EmptyState icon={FileText} title="Aucune note de perception" hint={filterRedevable || filterObs !== "all" ? "Aucun résultat pour ce filtre." : "Créez une note de perception à partir d'un assujetti."} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>N°</TableHead>
                  <TableHead>Redevable</TableHead>
                  <TableHead>Acte</TableHead>
                  <TableHead>Mois / Année</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Observation</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((n) => (
                  <TableRow key={n.id} className="hover:bg-slate-50" data-testid="note-debit-row">
                    <TableCell className="font-mono text-xs font-semibold text-slate-700">{n.numero}</TableCell>
                    <TableCell className="font-medium text-slate-800">{n.redevable}</TableCell>
                    <TableCell className="text-slate-600">{n.acte_generateur_nom}</TableCell>
                    <TableCell className="text-slate-600">{n.mois} {n.annee}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{fmtDate(n.date)}</TableCell>
                    <TableCell className="text-xs text-slate-600" data-testid="note-observation">{n.observation || "Paiement normal"}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-slate-900">{fmtMontant(n.montant, n.monnaie)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/print/note-debit/${n.id}`)} data-testid="print-note-debit-btn">
                        <Printer className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => window.open(`${API}/notes-debit/${n.id}/pdf`, "_blank")} data-testid="pdf-note-debit-btn">
                        <FileDown className="h-4 w-4 text-slate-600" />
                      </Button>
                      {canWrite && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(n)} data-testid="edit-note-debit-btn">
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setConfirm(n)} data-testid="delete-note-debit-btn">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {!loading && <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={PAGE_SIZE} onPage={setPage} />}
      </Card>

      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="note-debit-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading">{editing ? "Modifier la note de perception" : "Nouvelle note de perception"}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-1.5">
              <Label>Redevable *</Label>
              <AssujettiCombobox
                value={form.assujetti_id}
                selectedLabel={selected ? `${selected.code} — ${selected.redevable}` : ""}
                onSelect={onSelectAssujetti}
              />
            </div>

            {selected && (
              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4" data-testid="assujetti-info">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Informations de l'assujetti</p>
                <InfoRow label="Code" value={selected.code} />
                <InfoRow label="Acte Générateur" value={selected.acte_generateur_nom} />
                <InfoRow label="Province" value={selected.province} />
                <InfoRow label="Ville" value={selected.ville} />
                <InfoRow label="Commune" value={selected.commune} />
                <InfoRow label="Quartier" value={selected.quartier} />
                <InfoRow label="Avenue" value={selected.avenue} />
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Note de Taxation *</Label>
                <Input value={form.note_taxation} onChange={(e) => setField("note_taxation", e.target.value)}
                  placeholder="Ex: EDEEG/5228" data-testid="field-note-taxation" />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="field-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Mois de déclaration</Label>
                <Select value={form.mois} onValueChange={(v) => setField("mois", v)}>
                  <SelectTrigger data-testid="field-mois"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOIS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Année</Label>
                <Input value={form.annee} onChange={(e) => setField("annee", e.target.value)} data-testid="field-annee" />
              </div>
              <div className="space-y-1.5">
                <Label>Monnaie</Label>
                <Select value={form.monnaie} onValueChange={(v) => setField("monnaie", v)}>
                  <SelectTrigger data-testid="field-monnaie"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONNAIES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Code (Taux Acte Générateur)</Label>
                <Input value={form.code_taux || ""} readOnly disabled placeholder="—" className="font-mono bg-slate-50" data-testid="field-code-taux" />
              </div>
              <div className="space-y-1.5">
                <Label>Taux</Label>
                <Input value={form.taux || ""} readOnly disabled placeholder="—" className="font-mono bg-slate-50" data-testid="field-taux" />
              </div>
              {isCode1 && (
                <>
                  <div className="space-y-1.5">
                    <Label>Pénalité</Label>
                    <Input type="number" step="0.01" value={form.penalite} onChange={(e) => setField("penalite", e.target.value)}
                      placeholder="0.00" className="font-mono" data-testid="field-penalite" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Nuitées vendues</Label>
                    <Input type="number" step="0.01" value={form.nuitees_vendues} onChange={(e) => setField("nuitees_vendues", e.target.value)}
                      placeholder="0.00" className="font-mono" data-testid="field-nuitees" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Repas et boissons vendus</Label>
                    <Input type="number" step="0.01" value={form.repas_boissons} onChange={(e) => setField("repas_boissons", e.target.value)}
                      placeholder="0.00" className="font-mono" data-testid="field-repas" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Montant *</Label>
                <Input type="number" step="0.01" value={form.montant}
                  onChange={(e) => setField("montant", e.target.value)}
                  readOnly={isCode1} disabled={isCode1}
                  placeholder="0.00" className={`font-mono ${isCode1 ? "bg-slate-50" : ""}`} data-testid="field-montant" />
                {isCode1 && <p className="text-[11px] text-slate-400">Somme automatique : Pénalité + Nuitées + Repas et boissons</p>}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Observation</Label>
              <Select value={form.observation} onValueChange={(v) => setField("observation", v)}>
                <SelectTrigger data-testid="field-observation"><SelectValue /></SelectTrigger>
                <SelectContent>{OBSERVATIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Note / Libellé *</Label>
              <Textarea value={form.note} onChange={(e) => setField("note", e.target.value)}
                placeholder="Libellé ou observation" data-testid="field-note" />
            </div>
            <p className="text-xs text-slate-400">
              Le montant en lettres est généré automatiquement et figure sur la note imprimée.
            </p>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setSheet(false)}>Annuler</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-note-debit-btn">Enregistrer</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Supprimer la note de perception ?"
        description={`La note ${confirm?.numero} sera définitivement supprimée.`}
        onConfirm={remove}
      />
    </div>
  );
}
